import { useState, useRef, useEffect } from 'react';

// ── Subtask progress mini-bar ─────────────────────────────────────────────
export function SubtaskProgress({ done, total }) {
  if (total === 0) return null;
  const pct = Math.round((done / total) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
      <div style={{ flex: 1, height: 3, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 2,
          background: pct === 100 ? '#16A34A' : 'var(--accent)',
          width: `${pct}%`,
          transition: 'width 0.3s ease',
        }} />
      </div>
      <span style={{
        fontSize: 10, fontWeight: 600,
        color: pct === 100 ? '#16A34A' : 'var(--text3)',
        minWidth: 28, textAlign: 'right',
      }}>{done}/{total}</span>
    </div>
  );
}

// ── Individual subtask row ────────────────────────────────────────────────
function SubtaskRow({ subtask, onToggle, onUpdate, onRemove }) {
  const [editing,  setEditing]  = useState(false);
  const [val,      setVal]      = useState(subtask.title);
  const [hovering, setHovering] = useState(false);
  const inputRef = useRef(null);

  // BUG 3 FIX: sync local `val` when subtask.title changes externally
  // (e.g. after an optimistic update from another component)
  useEffect(() => {
    if (!editing) setVal(subtask.title);
  }, [subtask.title, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commitEdit = () => {
    const trimmed = val.trim();
    if (trimmed && trimmed !== subtask.title) {
      onUpdate(subtask.id, trimmed);
    } else {
      setVal(subtask.title); // reset to current title if empty or unchanged
    }
    setEditing(false);
  };

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 4px', borderRadius: 6,
        background: hovering ? 'var(--hover)' : 'transparent',
        transition: 'background 0.1s',
      }}
    >
      {/* Checkbox */}
      <div
        onClick={() => onToggle(subtask.id)}
        style={{
          width: 14, height: 14, borderRadius: 3, flexShrink: 0,
          border: subtask.done ? 'none' : '1.5px solid var(--border)',
          background: subtask.done ? 'var(--accent)' : 'transparent',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
        }}
      >
        {subtask.done && (
          <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
            <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>

      {/* Title / inline edit input */}
      {editing ? (
        <input
          ref={inputRef}
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={e => {
            if (e.key === 'Enter')  { e.preventDefault(); commitEdit(); }
            if (e.key === 'Escape') { setVal(subtask.title); setEditing(false); }
          }}
          style={{
            flex: 1, border: 'none', outline: 'none',
            background: 'var(--input-bg)', borderRadius: 4,
            padding: '1px 6px', fontSize: 12,
            color: 'var(--text)', fontFamily: 'inherit',
          }}
        />
      ) : (
        <span
          onDoubleClick={() => setEditing(true)}
          style={{
            flex: 1, fontSize: 12,
            color: subtask.done ? 'var(--text3)' : 'var(--text2)',
            textDecoration: subtask.done ? 'line-through' : 'none',
            cursor: 'default', lineHeight: 1.4,
            transition: 'all 0.15s',
          }}
        >{subtask.title}</span>
      )}

      {/* Hover actions */}
      {hovering && !editing && (
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          <button
            onClick={() => setEditing(true)}
            title="Edit"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text3)', padding: '2px 4px', borderRadius: 4,
              display: 'flex', alignItems: 'center',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button
            onClick={() => onRemove(subtask.id)}
            title="Delete"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text3)', padding: '2px 4px', borderRadius: 4,
              display: 'flex', alignItems: 'center',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ── Add subtask input ─────────────────────────────────────────────────────
// BUG 4 FIX: removed unused `focus` state — was set but never read
function AddSubtaskInput({ onAdd }) {
  const [val, setVal] = useState('');
  const ref = useRef(null);

  const submit = async () => {
    const trimmed = val.trim();
    if (!trimmed) return;
    await onAdd(trimmed);
    setVal('');
    ref.current?.focus();
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '4px 4px', marginTop: 2,
    }}>
      <div style={{
        width: 14, height: 14, borderRadius: 3, flexShrink: 0,
        border: '1.5px dashed var(--border)',
      }} />
      <input
        ref={ref}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter')  { e.preventDefault(); submit(); }
          if (e.key === 'Escape') { setVal(''); ref.current?.blur(); }
        }}
        placeholder="Add subtask…"
        style={{
          flex: 1, border: 'none', outline: 'none',
          background: 'transparent', fontSize: 12,
          color: 'var(--text)', fontFamily: 'inherit',
        }}
      />
      {val.trim() && (
        <button onClick={submit} style={{
          background: 'var(--accent)', border: 'none', borderRadius: 4,
          color: 'white', fontSize: 10, fontWeight: 700,
          padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit',
        }}>Add</button>
      )}
    </div>
  );
}

// ── Main SubtaskList ──────────────────────────────────────────────────────
export default function SubtaskList({ subtasks, onToggle, onUpdate, onRemove, onAdd }) {
  return (
    <div style={{ marginTop: 6, paddingLeft: 2 }}>
      {subtasks.map(s => (
        <SubtaskRow
          key={s.id}
          subtask={s}
          onToggle={onToggle}
          onUpdate={onUpdate}
          onRemove={onRemove}
        />
      ))}
      <AddSubtaskInput onAdd={onAdd} />
    </div>
  );
}